import SectionHeader from '@/components/SectionHeader';
import { SimpleReveal } from '@/hooks/useSimpleAnimation';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSwipeNavigation } from '@/hooks/useSwipeNavigation';
import { cn } from '@/lib/utils';
import { useHomeData } from '../../hooks/useHomeData';
import ManufacturingCard from '../ManufacturingCard';

export const HomeManufacturingSection = () => {
  const { manufacturing } = useHomeData();
  const isMobile = useIsMobile();
  
  const {
    activeIndex,
    setActiveIndex,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    isTransitioning,
    dragOffsetPercent,
    isDragging,
    containerRef
  } = useSwipeNavigation(manufacturing.length);

  return (
    <section id="manufacturing-quality" className="section-spacing bg-neutral-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SimpleReveal>
          <SectionHeader title="EU-GMP Facility" subtitle="Manufacturing & Quality" className="mb-6" />
          <p className="text-section-description mb-12">
            At Xenira, pharmaceutical production is built on EU-GMP compliance, regulatory alignment, and scientific rigor.
          </p>
        </SimpleReveal>
        
        <SimpleReveal delay={200}>
          {/* Desktop and tablet: Grid layout */}
          <div className="hidden sm:block">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {manufacturing.map((item, index) => (
                <ManufacturingCard
                  key={item.id}
                  title={item.title}
                  brand={item.brand}
                  description={item.description}
                  imageUrl={item.imageUrl}
                  altText={item.altText}
                  delay={index * 100}
                />
              ))}
            </div>
          </div>

            {/* Mobile: Swipe implementation with dots */}
            <div className="block sm:hidden">
              <div ref={containerRef} className="relative -mx-4 sm:mx-0">
              <div 
                className={cn(
                  "flex transform-gpu touch-pan-y md:touch-auto",
                  isDragging ? "transition-none" : "transition-transform duration-[500ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
                )}
                style={{ 
                  transform: `translate3d(calc(-${activeIndex * 100}% + ${dragOffsetPercent}%), 0, 0)`
                }}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                {manufacturing.map((item, index) => (
                  <div 
                    key={item.id} 
                    className={`basis-full shrink-0 px-6 py-2 transition-all duration-[500ms] ${
                      index === activeIndex ? 'opacity-100 scale-100' : 'opacity-70 scale-[0.98]'
                    }`}
                  >
                    <ManufacturingCard
                      title={item.title}
                      brand={item.brand}
                      description={item.description}
                      imageUrl={item.imageUrl}
                      altText={item.altText}
                      isMobile={true}
                    />
                  </div>
                ))}
              </div>
            </div>
            
            {/* Navigation Dots */}
            <div className="flex justify-center mt-8 space-x-3">
              {manufacturing.map((item, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  disabled={isTransitioning}
                  aria-pressed={index === activeIndex}
                  aria-label={`View manufacturing facility ${index + 1} of ${manufacturing.length}: ${item.title}`}
                  className={`h-2 rounded-full transition-all duration-300 shadow-sm disabled:cursor-not-allowed ${
                    index === activeIndex ? 'icon-gradient-red w-8' : 'bg-gray-300 w-2 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </SimpleReveal>
      </div>
    </section>
  );
};
